import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Loader2, Plus, Radar, RefreshCw, Search, Trash2, X, Shield,
  AlertTriangle, CheckCircle2, HelpCircle, Zap,
} from "lucide-react";
import { cn } from "@/lib/util";
import { usePermissions } from "@/lib/permissions";

/**
 * Third-Party Risk · main dashboard.
 * Standalone module — reads/writes only the /api/tpr/* endpoints.
 */
export default function ThirdPartyRisk() {
  const { isClient } = usePermissions();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState({});     // supplier_id → bool
  const [showAdd, setShowAdd] = useState(false);
  const [showDiscover, setShowDiscover] = useState(false);
  const [showDetail, setShowDetail] = useState(null); // supplier obj

  useEffect(() => {
    api.get("/clients").then((r) => {
      setClients(r.data || []);
      if ((r.data || []).length && !clientId) setClientId(r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const client = useMemo(
    () => clients.find((c) => c.id === clientId) || null,
    [clients, clientId],
  );

  const loadSuppliers = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await api.get(`/tpr/suppliers?client_id=${clientId}`);
      setSuppliers(r.data || []);
    } catch (e) {
      toast.error("No se pudieron cargar los proveedores");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  const scanSupplier = async (s) => {
    setScanning((prev) => ({ ...prev, [s.id]: { running: true, done: 0, total: 0 } }));
    try {
      const r = await api.post(`/tpr/suppliers/${s.id}/scan`);
      const scanId = r.data.id;
      // Poll every 2s until status transitions out of "running"
      let done = false;
      while (!done) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 2000));
        try {
          // eslint-disable-next-line no-await-in-loop
          const sr = await api.get(`/tpr/scans/${scanId}`);
          const st = sr.data;
          setScanning((prev) => ({
            ...prev,
            [s.id]: {
              running: st.status === "running",
              done: st.targets_completed || 0,
              total: st.targets_expected || 0,
            },
          }));
          if (st.status === "done") {
            toast.success(
              `${s.name}: reputación ${st.reputation?.status || "?"} · ${st.reputation?.target_count || 0} objetivos evaluados`,
            );
            done = true;
          } else if (st.status === "error") {
            toast.error(`El scan de ${s.name} falló: ${st.error || "error"}`);
            done = true;
          }
        } catch {
          /* transient error, keep polling */
        }
      }
      loadSuppliers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "El scan no pudo iniciar");
    } finally {
      setScanning((prev) => {
        const next = { ...prev };
        delete next[s.id];
        return next;
      });
    }
  };

  const deleteSupplier = async (s) => {
    if (!window.confirm(`¿Eliminar proveedor "${s.name}"?`)) return;
    try {
      await api.delete(`/tpr/suppliers/${s.id}`);
      toast.success("Proveedor eliminado");
      loadSuppliers();
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const counts = useMemo(() => {
    const c = { total: suppliers.length, red: 0, yellow: 0, green: 0, unknown: 0 };
    for (const s of suppliers) c[s.last_status] = (c[s.last_status] || 0) + 1;
    return c;
  }, [suppliers]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tpr-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="overline mb-2">Módulo · Riesgo de Terceros</div>
          <h1 className="font-display text-4xl font-medium tracking-tight">
            Reputación de proveedores
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Monitorea la reputación de cada proveedor asociado al cliente. Solo
            inteligencia de amenazas — sin escaneo de puertos ni vulnerabilidades.
          </p>
        </div>
      </header>

      {/* Client selector + summary */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-2">
          <div className="overline mb-1">Cliente</div>
          {isClient ? (
            <div className="px-3 py-2 text-sm font-mono border border-border bg-background/40" data-testid="tpr-client-name">
              {client?.name || "—"}
            </div>
          ) : (
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
            data-testid="tpr-client-select"
          >
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          )}
          {client && (
            <div className="text-[10px] font-mono text-muted-foreground mt-1">
              Dominio raíz: {(client.root_domains || [])[0] || "sin dominio"}
            </div>
          )}
        </div>
        <StatCard label="Total" value={counts.total} tone="muted" />
        <StatCard label="🟢 Verdes" value={counts.green || 0} tone="sev-low" />
        <StatCard label="🟡 Amarillos" value={counts.yellow || 0} tone="sev-medium" />
        <StatCard label="🔴 Rojos" value={counts.red || 0} tone="sev-critical" />
      </div>

      {/* Action bar */}
      <div className="flex items-center justify-between border-y border-border py-3">
        <div className="text-xs text-muted-foreground">
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Cargando…
            </span>
          ) : (
            <span>{suppliers.length} proveedor(es) registrados</span>
          )}
        </div>
        {!isClient && <div className="flex gap-2">
          <button
            onClick={() => setShowAdd(true)}
            disabled={!clientId}
            className="inline-flex items-center gap-2 border border-border bg-background px-3 py-2 font-mono text-[10px] tracking-widest uppercase hover:bg-muted disabled:opacity-50"
            data-testid="tpr-add-btn"
          >
            <Plus className="w-3 h-3" /> Agregar Proveedor
          </button>
          <button
            onClick={() => setShowDiscover(true)}
            disabled={!clientId || !(client?.root_domains || []).length}
            className="inline-flex items-center gap-2 border border-primary/60 bg-primary/15 text-primary px-3 py-2 font-mono text-[10px] tracking-widest uppercase hover:bg-primary/25 disabled:opacity-50"
            data-testid="tpr-discover-btn"
            title={
              (client?.root_domains || []).length
                ? "Descubre proveedores externos a partir del dominio raíz"
                : "El cliente debe tener un dominio raíz"
            }
          >
            <Search className="w-3 h-3" /> Descubrir Proveedores
          </button>
        </div>}
      </div>

      {/* Table */}
      <div className="border border-border bg-card/40 overflow-x-auto">
        <table className="w-full text-sm" data-testid="tpr-suppliers-table">
          <thead className="bg-background/60 border-b border-border">
            <tr className="text-left font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
              <th className="px-4 py-3">Proveedor</th>
              <th className="px-4 py-3">Dominio</th>
              <th className="px-4 py-3">Origen</th>
              <th className="px-4 py-3">Reputación</th>
              <th className="px-4 py-3">Último scan</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="text-center py-10 text-muted-foreground">
                  <div className="flex flex-col items-center gap-3">
                    <Building2 className="w-10 h-10 opacity-40" />
                    <div className="text-sm">
                      Este cliente no tiene proveedores registrados todavía.
                    </div>
                    {!isClient && (
                      <div className="font-mono text-[10px] tracking-wider uppercase">
                        Agrega uno manualmente o lanza el descubrimiento automático.
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {suppliers.map((s) => (
              <tr
                key={s.id}
                className="border-b border-border hover:bg-muted/20 cursor-pointer"
                onClick={() => setShowDetail(s)}
                data-testid={`tpr-row-${s.id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-medium">{s.name}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {s.domain}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={cn(
                      "font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 border",
                      s.source === "discovered"
                        ? "text-primary border-primary/40 bg-primary/10"
                        : "text-muted-foreground border-border bg-background/40",
                    )}
                  >
                    {s.source === "discovered" ? "auto" : "manual"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={s.last_status} />
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                  {s.last_scan_at
                    ? new Date(s.last_scan_at).toLocaleString("es-ES")
                    : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  {isClient ? (
                    <span className="font-mono text-[9px] tracking-widest uppercase text-muted-foreground">Ver detalle</span>
                  ) : (<>
                  <button
                    onClick={(e) => { e.stopPropagation(); scanSupplier(s); }}
                    disabled={!!scanning[s.id]}
                    className="inline-flex items-center gap-1 border border-primary/40 text-primary px-2 py-1 font-mono text-[9px] tracking-widest uppercase hover:bg-primary/10 disabled:opacity-70"
                    data-testid={`tpr-rescan-${s.id}`}
                    title="Barrido reputacional multi-objetivo (dominio + subdominios + IPs)"
                  >
                    {scanning[s.id]?.running ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        {scanning[s.id].total > 0
                          ? `${scanning[s.id].done}/${scanning[s.id].total}`
                          : "…"}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3" />
                        Rescan
                      </>
                    )}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteSupplier(s); }}
                    className="inline-flex items-center gap-1 ml-2 border border-border px-2 py-1 font-mono text-[9px] tracking-widest uppercase text-muted-foreground hover:text-sev-critical hover:border-sev-critical/40"
                    data-testid={`tpr-delete-${s.id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                  </>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddSupplierModal
          clientId={clientId}
          onClose={() => setShowAdd(false)}
          onCreated={() => { loadSuppliers(); setShowAdd(false); }}
        />
      )}
      {showDiscover && (
        <DiscoveryModal
          client={client}
          onClose={() => setShowDiscover(false)}
          onApplied={() => { loadSuppliers(); setShowDiscover(false); }}
        />
      )}
      {showDetail && (
        <SupplierDetailModal
          isClient={isClient}
          supplier={showDetail}
          onClose={() => setShowDetail(null)}
        />
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================
function StatCard({ label, value, tone }) {
  const toneCls = {
    "sev-low": "text-sev-low border-sev-low/40 bg-sev-low/10",
    "sev-medium": "text-sev-medium border-sev-medium/40 bg-sev-medium/10",
    "sev-critical": "text-sev-critical border-sev-critical/40 bg-sev-critical/10",
    muted: "text-muted-foreground border-border bg-background/40",
  }[tone] || "text-muted-foreground border-border bg-background/40";
  return (
    <div className={cn("border p-2", toneCls)}>
      <div className="font-display text-2xl font-medium">{value}</div>
      <div className="font-mono text-[9px] tracking-widest uppercase">{label}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    green: { cls: "text-sev-low border-sev-low/40 bg-sev-low/10", icon: CheckCircle2, label: "Verde" },
    yellow: { cls: "text-sev-medium border-sev-medium/40 bg-sev-medium/10", icon: AlertTriangle, label: "Amarillo" },
    red: { cls: "text-sev-critical border-sev-critical/40 bg-sev-critical/10", icon: Shield, label: "Rojo" },
    unknown: { cls: "text-muted-foreground border-border bg-background/40", icon: HelpCircle, label: "Sin datos" },
  };
  const m = map[status] || map.unknown;
  const Icon = m.icon;
  return (
    <span
      className={cn("inline-flex items-center gap-1 font-mono text-[10px] tracking-widest uppercase border px-2 py-0.5", m.cls)}
      data-testid={`tpr-status-${status}`}
    >
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

// ============================================================================
// Add manual supplier
// ============================================================================
function AddSupplierModal({ clientId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/tpr/suppliers", { client_id: clientId, name, domain });
      toast.success("Proveedor agregado");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo agregar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Nuevo proveedor · manual" onClose={onClose} testId="tpr-add-modal">
      <form onSubmit={submit} className="space-y-3 p-5">
        <label className="block">
          <div className="overline mb-1">Nombre comercial</div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={1}
            className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"
            placeholder="Ej: Stripe"
            data-testid="tpr-add-name"
          />
        </label>
        <label className="block">
          <div className="overline mb-1">Dominio principal</div>
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            required
            className="w-full bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
            placeholder="stripe.com"
            data-testid="tpr-add-domain"
          />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy || !name || !domain}
            className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="tpr-add-submit"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            Agregar
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ============================================================================
// Discovery modal
// ============================================================================
function DiscoveryModal({ client, onClose, onApplied }) {
  const [runId, setRunId] = useState(null);
  const [run, setRun] = useState(null);
  const [selected, setSelected] = useState({});    // domain -> {name, checked}
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!client?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post("/tpr/discover", { client_id: client.id });
        if (cancelled) return;
        setRunId(r.data.id);
        setRun(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "No se pudo lanzar descubrimiento");
        onClose();
      }
    })();
    return () => { cancelled = true; };
  }, [client]);   // eslint-disable-line

  useEffect(() => {
    if (!runId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/tpr/discover/${runId}`);
        if (cancelled) return;
        setRun(r.data);
        if (r.data.status === "running") {
          setTimeout(tick, 2000);
        } else if (r.data.status === "done") {
          const init = {};
          for (const c of r.data.candidates || []) {
            init[c.domain] = { name: c.domain, checked: c.is_known_cdn };
          }
          setSelected(init);
        }
      } catch { /* keep polling silently */ }
    };
    tick();
    return () => { cancelled = true; };
  }, [runId]);

  const apply = async () => {
    const list = Object.entries(selected)
      .filter(([, v]) => v.checked)
      .map(([domain, v]) => ({ domain, name: v.name || domain }));
    if (list.length === 0) {
      toast.error("Selecciona al menos un candidato");
      return;
    }
    setApplying(true);
    try {
      const r = await api.post(`/tpr/discover/${runId}/apply`, { selected: list });
      toast.success(`${r.data.created_count} proveedor(es) agregado(s)`);
      onApplied();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar");
    } finally {
      setApplying(false);
    }
  };

  const isRunning = run?.status === "running" || !run;
  const candidates = run?.candidates || [];
  const rootDomain = client?.root_domains?.[0] || "?";

  return (
    <ModalShell title="Descubrir proveedores" onClose={onClose} testId="tpr-discover-modal">
      <div className="p-5 space-y-4">
        <div className="text-xs text-muted-foreground">
          Buscando servicios externos (CNAME + redirects + assets) a partir de{" "}
          <code className="font-mono text-primary">{rootDomain}</code> usando{" "}
          <span className="font-mono">crt.sh</span> + <span className="font-mono">httpx</span>.
        </div>

        {isRunning && (
          <div className="border border-border bg-background/40 p-6 text-center flex flex-col items-center gap-3">
            <Radar className="w-8 h-8 text-primary animate-pulse" />
            <div className="font-mono text-[10px] tracking-widest uppercase">
              Descubriendo…
            </div>
            <div className="text-xs text-muted-foreground">
              El proceso puede tardar 30-60s (crt.sh + resolución CNAME + fetch root)
            </div>
          </div>
        )}

        {run?.status === "error" && (
          <div className="border border-sev-critical/40 bg-sev-critical/5 p-4 text-sm text-sev-critical">
            El descubrimiento falló: {run.error || "error desconocido"}
          </div>
        )}

        {run?.status === "done" && (
          <>
            <div className="text-xs text-muted-foreground">
              <b className="text-foreground">{candidates.length}</b> candidatos únicos.
              Los marcados como CDN conocido vienen pre-seleccionados.
            </div>
            {candidates.length === 0 ? (
              <div className="border border-border bg-background/40 p-6 text-center text-sm text-muted-foreground">
                No se encontraron proveedores externos relevantes.
              </div>
            ) : (
              <div className="border border-border max-h-[45vh] overflow-y-auto">
                {candidates.map((c) => {
                  const sel = selected[c.domain] || { name: c.domain, checked: false };
                  return (
                    <label
                      key={c.domain}
                      className="flex items-start gap-3 p-3 border-b border-border hover:bg-muted/20 cursor-pointer"
                      data-testid={`tpr-candidate-${c.domain}`}
                    >
                      <input
                        type="checkbox"
                        checked={sel.checked}
                        onChange={(e) =>
                          setSelected((prev) => ({
                            ...prev,
                            [c.domain]: { ...sel, checked: e.target.checked },
                          }))
                        }
                        className="mt-1 accent-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm">{c.domain}</span>
                          <span className="font-mono text-[9px] tracking-widest uppercase border border-border px-2 py-0.5 text-muted-foreground">
                            {c.kind}
                          </span>
                          {c.is_known_cdn && (
                            <span className="font-mono text-[9px] tracking-widest uppercase border border-primary/40 text-primary bg-primary/10 px-2 py-0.5">
                              CDN conocido
                            </span>
                          )}
                          <span className="font-mono text-[10px] text-muted-foreground">
                            {c.hits} señal(es)
                          </span>
                        </div>
                        <div className="mt-1 font-mono text-[10px] text-muted-foreground line-clamp-2">
                          {(c.evidence || []).slice(0, 2).join(" · ")}
                        </div>
                        {sel.checked && (
                          <input
                            type="text"
                            value={sel.name}
                            onChange={(e) =>
                              setSelected((prev) => ({
                                ...prev,
                                [c.domain]: { ...sel, name: e.target.value },
                              }))
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="mt-2 w-full bg-background border border-border px-2 py-1 text-xs font-mono focus:border-primary outline-none"
                            placeholder="Nombre del proveedor"
                          />
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={apply}
                disabled={applying || candidates.length === 0}
                className="bg-primary text-primary-foreground px-4 py-2 text-sm hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="tpr-discover-apply"
              >
                {applying ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Zap className="w-3 h-3" />
                )}
                Añadir seleccionados
              </button>
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}

// ============================================================================
// Detail modal (multi-target reputational breakdown)
// ============================================================================
function SupplierDetailModal({ supplier, onClose, isClient }) {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // target -> bool

  useEffect(() => {
    api
      .get(`/tpr/suppliers/${supplier.id}/scans?limit=10`)
      .then((r) => setScans(r.data || []))
      .catch(() => setScans([]))
      .finally(() => setLoading(false));
  }, [supplier.id]);

  const latest = scans[0];
  const targets = latest?.reputation?.targets || [];
  const bad = targets.filter((t) => t.status === "red" || t.status === "yellow");
  const good = targets.filter((t) => t.status === "green");
  const unknown = targets.filter((t) => t.status === "unknown");

  const targetIcon = (kind) => {
    if (kind === "ip") return "🌐";
    if (kind === "domain") return "🏢";
    return "🔗"; // subdomain
  };

  return (
    <ModalShell
      title={supplier.name}
      subtitle={supplier.domain}
      onClose={onClose}
      testId="tpr-detail-modal"
    >
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <StatusPill status={supplier.last_status} />
          <span className="font-mono text-[10px] text-muted-foreground">
            Origen · {supplier.source === "discovered" ? "auto (crt.sh + httpx)" : "manual"}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin inline-block" />
          </div>
        ) : !latest ? (
          <div className="border border-border bg-background/40 p-6 text-center text-sm text-muted-foreground">
            {isClient
              ? "Aún no hay resultados de reputación para este proveedor."
              : "Aún no se ha ejecutado ningún scan. Usa el botón \"Rescan\" en la tabla."}
          </div>
        ) : latest.status === "running" ? (
          <div className="border border-primary/40 bg-primary/5 p-6 text-center">
            <Loader2 className="w-6 h-6 animate-spin inline-block text-primary" />
            <div className="mt-2 font-mono text-[11px] tracking-widest uppercase text-primary">
              Barrido en curso · {latest.targets_completed}/{latest.targets_expected} objetivos
            </div>
          </div>
        ) : (
          <>
            {/* Aggregate summary */}
            <div className="grid grid-cols-4 gap-2">
              <StatCard label="Objetivos" value={targets.length} tone="muted" />
              <StatCard label="Subdominios" value={latest.reputation?.subdomain_count ?? 0} tone="muted" />
              <StatCard label="IPs" value={latest.reputation?.ip_count ?? 0} tone="muted" />
              <StatCard
                label="Con banderas"
                value={bad.length}
                tone={bad.length > 0 ? "sev-critical" : "sev-low"}
              />
            </div>

            {/* Bad targets — always shown expanded */}
            {bad.length > 0 && (
              <div>
                <div className="overline mb-2 text-sev-critical">
                  Objetivos con reputación afectada · {bad.length}
                </div>
                <div className="space-y-2">
                  {bad.map((t) => (
                    <TargetRow key={t.target} t={t} icon={targetIcon(t.kind)} defaultOpen />
                  ))}
                </div>
              </div>
            )}

            {/* Green + unknown collapsed group */}
            {(good.length > 0 || unknown.length > 0) && (
              <div>
                <button
                  type="button"
                  onClick={() => setExpanded((p) => ({ ...p, _rest: !p._rest }))}
                  className="overline hover:text-foreground"
                >
                  {expanded._rest ? "▾" : "▸"} Resto de objetivos ·{" "}
                  {good.length} limpios · {unknown.length} sin datos
                </button>
                {expanded._rest && (
                  <div className="space-y-2 mt-2">
                    {[...good, ...unknown].map((t) => (
                      <TargetRow key={t.target} t={t} icon={targetIcon(t.kind)} />
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="text-[10px] font-mono text-muted-foreground pt-2 border-t border-border">
              Último scan · {new Date(latest.finished_at || latest.created_at).toLocaleString("es-ES")}
              {scans.length > 1 && `  ·  ${scans.length} scans en historial`}
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
}

function TargetRow({ t, icon, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className={cn(
        "border",
        t.status === "red"
          ? "border-sev-critical/40 bg-sev-critical/5"
          : t.status === "yellow"
          ? "border-sev-medium/40 bg-sev-medium/5"
          : t.status === "green"
          ? "border-sev-low/30 bg-background/40"
          : "border-border bg-background/40",
      )}
      data-testid={`tpr-target-${t.target}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-2 text-left"
      >
        <span className="flex items-center gap-2 min-w-0">
          <span>{icon}</span>
          <span className="font-mono text-[11px] truncate">{t.target}</span>
          <span className="font-mono text-[9px] tracking-widest uppercase border border-border px-1.5 py-0.5 text-muted-foreground">
            {t.kind}
          </span>
        </span>
        <span className="flex items-center gap-2 shrink-0">
          {t.malicious_count > 0 && (
            <span className="font-mono text-[10px] text-sev-critical">
              ✕ {t.malicious_count}
            </span>
          )}
          {t.suspicious_count > 0 && (
            <span className="font-mono text-[10px] text-sev-medium">
              ⚠ {t.suspicious_count}
            </span>
          )}
          <StatusPill status={t.status} />
          <span className="font-mono text-[9px] text-muted-foreground">{open ? "▾" : "▸"}</span>
        </span>
      </button>
      {open && (
        <div className="border-t border-border/60 px-2 py-2 space-y-1">
          {(t.sources || []).length === 0 && (
            <div className="font-mono text-[10px] text-muted-foreground italic">
              Sin datos de fuentes
            </div>
          )}
          {(t.sources || []).map((s) => (
            <div
              key={s.id}
              className="flex items-center justify-between text-[10px] font-mono"
            >
              <span className="truncate">
                {s.label}
                {s.signal && (
                  <span className="text-muted-foreground"> — {s.signal}</span>
                )}
                {s.reason && (
                  <span className="text-muted-foreground italic"> ({s.reason})</span>
                )}
              </span>
              <span
                className={cn(
                  "shrink-0 ml-2 font-mono text-[9px] tracking-widest uppercase border px-1.5 py-0.5",
                  s.verdict === "malicious"
                    ? "text-sev-critical border-sev-critical/40 bg-sev-critical/10"
                    : s.verdict === "suspicious"
                    ? "text-sev-medium border-sev-medium/40 bg-sev-medium/10"
                    : s.verdict === "clean"
                    ? "text-sev-low border-sev-low/40 bg-sev-low/10"
                    : "text-muted-foreground border-border bg-background/40",
                )}
              >
                {s.verdict}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Modal shell
// ============================================================================
function ModalShell({ title, subtitle, onClose, children, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-background/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-card border border-border max-h-[92vh] flex flex-col"
        data-testid={testId}
      >
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <div className="font-display text-xl font-medium">{title}</div>
            {subtitle && (
              <div className="font-mono text-[11px] text-muted-foreground mt-1">
                {subtitle}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-muted"
            data-testid={`${testId}-close`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
